import React from 'react';
import './Cookies.css';

const COOKIE_ROWS = [
  {
    id: 'session',
    cookie: 'flotte_session',
    provider: 'Flotte',
    purpose: 'Mantener tu sesión activa, recordar el idioma y aplicar los permisos correctos en cada módulo.',
    duration: 'Sesión',
    type: 'Propias',
  },
  {
    id: 'consent',
    cookie: 'flotte_consent',
    provider: 'Flotte',
    purpose: 'Registrar la preferencia de consentimiento para que no tengas que reconfirmarla en cada visita.',
    duration: '12 meses',
    type: 'Propias',
  },
  {
    id: 'ga',
    cookie: '_ga',
    provider: 'Google Analytics',
    purpose: 'Medir rutas de navegación y detectar puntos de fricción en el embudo de captación.',
    duration: '2 años',
    type: 'Terceros',
  },
  {
    id: 'gid',
    cookie: '_gid',
    provider: 'Google Analytics',
    purpose: 'Comparar el rendimiento diario y detectar errores recurrentes en sesiones consecutivas.',
    duration: '24 horas',
    type: 'Terceros',
  },
  {
    id: 'stripe',
    cookie: '_stripe_mid',
    provider: 'Stripe',
    purpose: 'Prevenir fraude y enlazar los intentos de pago con su evaluación de riesgo en tiempo real.',
    duration: '1 año',
    type: 'Terceros',
  },
  {
    id: 'intercom',
    cookie: 'intercom-device-id',
    provider: 'Intercom',
    purpose: 'Ofrecer soporte contextual y recordar el estado de conversaciones abiertas con el equipo de éxito.',
    duration: '9 meses',
    type: 'Terceros',
  },
  {
    id: 'hotjar',
    cookie: '_hjSessionUser',
    provider: 'Hotjar',
    purpose: 'Generar mapas de calor agregados y estudios de usabilidad para optimizar formularios críticos.',
    duration: '1 año',
    type: 'Terceros',
  },
];

const CATEGORY_SECTIONS = [
  {
    id: 'essential',
    icon: '🔒',
    title: 'Esenciales',
    description:
      'Son imprescindibles para autenticar usuarios, equilibrar la carga del servicio y mantener configuraciones mínimas. Si se desactivan, Flotte no podría identificar tu sesión ni respetar tus permisos.',
    bullets: [
      'Garantizan flujos de inicio de sesión seguros y verifican la integridad de cada solicitud.',
      'Recuerdan datos básicos como idioma, rol y compañía activa para mostrar la consola correcta.',
      'Registran tu consentimiento y sincronizan la respuesta con nuestros sistemas de cumplimiento.',
    ],
  },
  {
    id: 'performance',
    icon: '📊',
    title: 'Rendimiento y Analítica',
    description:
      'Nos permiten cuantificar métricas de uso (tiempo en página, conversiones, abandono) y así priorizar optimizaciones que reduzcan fricciones y errores operativos.',
    bullets: [
      'Agrupan eventos de navegación para entender qué módulos requieren más capacidad.',
      'Detectan errores recurrentes y variaciones inusuales en flujos de registro o publicación.',
      'Ayudan a decidir nuevas funcionalidades usando datos agregados y anonimizados.',
    ],
  },
  {
    id: 'marketing',
    icon: '📢',
    title: 'Marketing',
    description:
      'Utilizamos un conjunto limitado de cookies para asegurar que los mensajes comerciales sean relevantes y respeten la frecuencia establecida.',
    bullets: [
      'Sincronizan campañas entre Flotte, anuncios de pago y correos automatizados.',
      'Restringen la cantidad de impactos publicitarios para evitar saturación.',
      'Nos permiten medir qué creativos generan registros o demos reales.',
    ],
  },
];

const MANAGEMENT_GUIDES = [
  {
    id: 'chrome',
    label: 'Google Chrome',
    description: 'Guía oficial para crear excepciones, borrar datos y bloquear cookies de terceros.',
    href: 'https://support.google.com/accounts/answer/61416?hl=es',
  },
  {
    id: 'safari',
    label: 'Safari (macOS / iOS)',
    description: 'Instrucciones de Apple para gestionar la prevención inteligente de rastreo.',
    href: 'https://support.apple.com/es-mx/guide/safari/sfri11471/mac',
  },
  {
    id: 'edge',
    label: 'Microsoft Edge',
    description: 'Pasos para limpiar cookies, configurar permisos por sitio y activar protección mejorada.',
    href: 'https://support.microsoft.com/es-es/microsoft-edge/eliminar-y-administrar-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09',
  },
];

const UPDATE_DATE = 'Actualizado: 08 de enero de 2025';

export default function Cookies() {
  return (
    <main className="cookies-doc">
      <header className="cookies-doc__hero">
        <div className="cookies-doc__hero-content">
          <p className="cookies-doc__eyebrow">Transparencia &amp; Control</p>
          <h1 className="cookies-doc__title">Política de Cookies</h1>
          <p className="cookies-doc__subtitle">
            Entendiendo cómo y por qué usamos tecnologías de seguimiento para mejorar tu experiencia en Flotte.
          </p>
          <p className="cookies-doc__update" aria-label="Fecha de actualización">
            {UPDATE_DATE}
          </p>
        </div>
      </header>

      <section className="cookies-doc__intro">
        <p>
          Las cookies son fragmentos mínimos de información que se almacenan en tu navegador para que podamos
          autenticarte, medir el rendimiento del producto y personalizar la experiencia. Solo utilizamos las categorías
          necesarias para operar Flotte, con controles internos que limitan su duración, acceso y finalidad.
        </p>
      </section>

      <section className="cookies-doc__inventory" aria-labelledby="cookie-inventory">
        <div className="cookies-doc__section-head">
          <p className="cookies-doc__kicker">Inventario</p>
          <h2 id="cookie-inventory">Tabla de cookies activas</h2>
          <p>
            Consulta el detalle de cada cookie, su proveedor y el motivo por el que se encuentra habilitada. Actualizamos
            esta tabla cada vez que incorporamos o retiramos una tecnología de medición.
          </p>
        </div>
        <div className="cookies-table__wrapper">
          <table className="cookies-table" role="table">
            <caption className="sr-only">Listado de cookies utilizadas en Flotte</caption>
            <thead>
              <tr>
                <th scope="col">Nombre/Proveedor</th>
                <th scope="col">Propósito</th>
                <th scope="col">Duración</th>
                <th scope="col">Tipo</th>
              </tr>
            </thead>
            <tbody>
              {COOKIE_ROWS.map(row => (
                <tr key={row.id}>
                  <td>
                    <span className="cookies-table__cookie">{row.cookie}</span>
                    <span className="cookies-table__provider">{row.provider}</span>
                  </td>
                  <td>{row.purpose}</td>
                  <td>{row.duration}</td>
                  <td>{row.type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="cookies-doc__categories">
        {CATEGORY_SECTIONS.map(category => (
          <article key={category.id} className="cookies-category" aria-labelledby={`${category.id}-title`}>
            <header className="cookies-category__head">
              <span className="cookies-category__icon" aria-hidden="true">
                {category.icon}
              </span>
              <h3 id={`${category.id}-title`}>{category.title}</h3>
            </header>
            <p>{category.description}</p>
            <ul>
              {category.bullets.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        ))}
      </section>

      <section className="cookies-doc__manage" aria-labelledby="cookies-manage">
        <div className="cookies-doc__section-head">
          <p className="cookies-doc__kicker">Preferencias</p>
          <h2 id="cookies-manage">¿Cómo gestionar mis preferencias?</h2>
          <p>
            Puedes ajustar o borrar cookies desde tu navegador en cualquier momento. Te compartimos las guías oficiales
            para hacerlo paso a paso y sin instalar extensiones adicionales.
          </p>
        </div>
        <ul className="cookies-manage__list">
          {MANAGEMENT_GUIDES.map(guide => (
            <li key={guide.id}>
              <div>
                <span className="cookies-manage__label">{guide.label}</span>
                <p>{guide.description}</p>
              </div>
              <a href={guide.href} target="_blank" rel="noopener noreferrer">
                Ver guía oficial
              </a>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
